import {
    createZodFormResolver,
    DateTimeService,
    DateTimeUtils,
    lt,
    useAppNowUtc,
    useFormControls,
    useTimezone,
} from '@mews/framework';
import { Button, IconName, Stack, APPBAR_DESKTOP_HEIGHT, Container } from '@optimus-web/core';
import { FilterSidebar } from '@mews/b2b-ui';
import { DateIntervalFields } from '@mews-commander/form';
import { SearchType } from '@mews-commander/enums';
import {
    createLedgerActivityReportFiltersSchema,
    FiltersValueKey,
    LedgerActivityReportUrlSearchValues,
    useDefaultLedgerActivityReportFilterValues,
} from '../schema';
import { SearchHookField } from '../../../components/form/hookFields';
import { useAccountLedgersEffectiveDateSuspenseQuery } from '../hooks/useAccountingLedgersEffectiveDateQuery';
import { useNavigateWithFilters } from '../hooks/useNavigateWithFilters';

interface LedgerActivityReportFiltersProps {
    open: boolean;
    toggleSidebar: () => void;
    filterValues: LedgerActivityReportUrlSearchValues;
}
export const LedgerActivityReportFilters = ({
    open,
    toggleSidebar,
    filterValues,
}: LedgerActivityReportFiltersProps) => {
    const defaultLedgerActivityReportFilterValues = useDefaultLedgerActivityReportFilterValues();
    const {
        data: { effectiveDate },
    } = useAccountLedgersEffectiveDateSuspenseQuery();
    const timezone = useTimezone();

    const minEffectiveDate = DateTimeUtils.dateTimeToStructuredDate(
        DateTimeService.parse(effectiveDate ?? '', timezone)
    );

    const appNowUtc = useAppNowUtc();
    const maxDate = DateTimeUtils.utcToStructuredDate(appNowUtc.value, timezone);

    const navigateWithFilters = useNavigateWithFilters();

    const handleSubmit = (values: LedgerActivityReportUrlSearchValues) => {
        navigateWithFilters(values, filterValues.sort);
    };
    const filtersSchema = createLedgerActivityReportFiltersSchema(effectiveDate ?? '', timezone);

    const { reset: resetFields, renderFormContent } = useFormControls({
        id: 'ledger-activity-report-filter',
        defaultValues: filterValues ?? defaultLedgerActivityReportFilterValues,
        mode: 'onChange',
        onSubmit: handleSubmit,
        resolver: createZodFormResolver(filtersSchema),
    });

    const reset = () => {
        navigateWithFilters(defaultLedgerActivityReportFilterValues, filterValues.sort);
        resetFields(defaultLedgerActivityReportFilterValues);
    };

    return renderFormContent(
        <Container position="sticky" top="0" height={`calc(100vh - ${APPBAR_DESKTOP_HEIGHT}px)`}>
            <FilterSidebar
                primaryButton={{
                    label: lt('ApplyFilters'),
                    type: 'submit',
                }}
                actions={
                    <Stack alignment="start" fill>
                        <Button icon={IconName.Refresh} onClick={reset} type="button" variant="ghost" size="medium">
                            {lt('ResetFilters')}
                        </Button>
                    </Stack>
                }
                hideOffset="300"
                show={open}
                title={lt('LedgerActivityReportFiltersTitle')}
                toggle={toggleSidebar}
            >
                <Stack spacing="300" vertical>
                    <DateIntervalFields
                        startDateProps={{
                            name: FiltersValueKey.startDate,
                            label: lt('StartDate'),
                        }}
                        endDateProps={{
                            name: FiltersValueKey.endDate,
                            label: lt('EndDate'),
                        }}
                        minDate={minEffectiveDate}
                        maxDate={maxDate}
                        mode="iso"
                        size="medium"
                    />
                    <SearchHookField
                        name="ownerId"
                        searchTypes={[SearchType.Company, SearchType.Customer]}
                        label={lt('Profile')}
                        size="medium"
                    />
                </Stack>
            </FilterSidebar>
        </Container>
    );
};
